import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import {
  LayoutDashboard,
  Users,
  Banknote,
  Receipt,
  Calculator,
  LogOut,
  ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Toaster } from "@/components/ui/sonner";

const NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/borrowers", label: "All Member", icon: Users, testid: "nav-borrowers" },
  { to: "/loans", label: "Borrowing List", icon: Banknote, testid: "nav-loans" },
  { to: "/payments", label: "Repayments", icon: Receipt, testid: "nav-payments" },
  { to: "/calculator", label: "Calculator", icon: Calculator, testid: "nav-calculator" },
];

export default function Layout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-[#0B0F17] grid-radial flex">
      {/* Sidebar */}
      <aside className="hidden md:flex md:w-64 lg:w-72 flex-col border-r border-white/5 bg-[#0E131E] px-4 py-6 sticky top-0 h-screen">
        <div className="flex items-center gap-3 px-2 mb-10">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-emerald-500 to-emerald-700 flex items-center justify-center shadow-lg shadow-emerald-900/40">
            <ShieldCheck className="h-5 w-5 text-white" strokeWidth={2.4} />
          </div>
          <div>
            <div className="font-display font-extrabold text-xl tracking-tight text-white leading-none">
              LendingPH
            </div>
            <div className="text-[10px] uppercase tracking-[0.22em] text-emerald-400 font-semibold mt-1">
              Admin Console
            </div>
          </div>
        </div>

        <nav className="flex-1 flex flex-col gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={n.testid}
              className={({ isActive }) =>
                `group flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all ${
                  isActive
                    ? "bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
                    : "text-slate-400 hover:text-white hover:bg-white/5 border border-transparent"
                }`
              }
            >
              <n.icon className="h-4 w-4" strokeWidth={2} />
              {n.label}
            </NavLink>
          ))}
        </nav>

        <div className="border-t border-white/5 pt-4 mt-4">
          <div className="px-2 mb-3">
            <div className="text-xs text-slate-500">Signed in as</div>
            <div className="text-sm font-medium text-white truncate" data-testid="current-user-email">
              {user?.email}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-400 font-semibold">
              {user?.role}
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            data-testid="logout-button"
            className="w-full justify-start text-slate-400 hover:text-white hover:bg-red-500/10"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Sign out
          </Button>
        </div>
      </aside>

      {/* Mobile top bar */}
      <div className="md:hidden fixed top-0 inset-x-0 z-40 glass border-b border-white/10 px-4 py-3 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="h-8 w-8 rounded-lg bg-emerald-600 flex items-center justify-center">
            <ShieldCheck className="h-4 w-4 text-white" />
          </div>
          <span className="font-display font-bold text-white">LendingPH</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={handleLogout}
          className="text-slate-300"
          data-testid="logout-button-mobile"
        >
          <LogOut className="h-4 w-4" />
        </Button>
      </div>

      {/* Main */}
      <main className="flex-1 min-w-0">
        {/* mobile nav */}
        <nav className="md:hidden fixed bottom-0 inset-x-0 z-40 glass border-t border-white/10 grid grid-cols-5 py-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              data-testid={`${n.testid}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center gap-0.5 py-2 text-[10px] font-semibold ${
                  isActive ? "text-emerald-400" : "text-slate-500"
                }`
              }
            >
              <n.icon className="h-5 w-5" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className={`pt-16 pb-24 md:pt-0 md:pb-0 px-4 sm:px-6 py-6 mx-auto ${pathname === "/borrowers" ? "max-w-none lg:px-6" : "max-w-7xl lg:px-10"}`}>
          <Outlet />
        </div>
      </main>
      <Toaster richColors position="top-right" theme="dark" />
    </div>
  );
}
